/**
 * Sanitizes numeric input to allow only valid number characters.
 * - Removes all non-numeric characters except decimal point and minus
 * - Ensures only one decimal point
 * - Limits to 1 decimal place
 */
export function sanitizeNumericInput(input: string): string {
  // Remove any non-numeric characters except decimal point and minus
  let sanitized = input.replace(/[^0-9.-]/g, "")

  // Ensure only one decimal point
  const parts = sanitized.split(".")
  if (parts.length > 2) {
    sanitized = parts[0] + "." + parts.slice(1).join("")
  }

  // Limit to 1 decimal place
  if (parts.length === 2 && parts[1].length > 1) {
    sanitized = parts[0] + "." + parts[1].slice(0, 1)
  }

  return sanitized
}

/**
 * Handles keydown events for numeric inputs.
 * Blocks all non-numeric characters including 'e', 'E', '+', etc.
 * Only allows: digits, decimal point, minus sign, and control keys.
 */
export function handleNumericKeyDown(event: React.KeyboardEvent<HTMLInputElement>): void {
  // Allow: backspace, delete, tab, escape, enter, arrows, home, end
  const allowedKeys = [
    "Backspace",
    "Delete",
    "Tab",
    "Escape",
    "Enter",
    "ArrowLeft",
    "ArrowRight",
    "ArrowUp",
    "ArrowDown",
    "Home",
    "End",
  ]
  if (allowedKeys.includes(event.key)) {
    return
  }

  // Allow Ctrl/Cmd + A, C, V, X
  if ((event.ctrlKey || event.metaKey) && ["a", "c", "v", "x"].includes(event.key.toLowerCase())) {
    return
  }

  // Allow: decimal point (only if not already present)
  if (event.key === "." && !event.currentTarget.value.includes(".")) {
    return
  }

  // Allow: minus sign (only at the start)
  if (
    event.key === "-" &&
    event.currentTarget.selectionStart === 0 &&
    !event.currentTarget.value.includes("-")
  ) {
    return
  }

  // Allow: numbers 0-9
  if (/^[0-9]$/.test(event.key)) {
    // Check if adding this digit would exceed 1 decimal place
    const value = event.currentTarget.value
    const selectionStart = event.currentTarget.selectionStart ?? value.length
    const decimalIndex = value.indexOf(".")
    if (decimalIndex !== -1 && selectionStart > decimalIndex) {
      const decimals = value.slice(decimalIndex + 1).length
      if (
        decimals >= 1 &&
        event.currentTarget.selectionStart === event.currentTarget.selectionEnd
      ) {
        event.preventDefault()
        return
      }
    }
    return
  }

  // Block everything else (including 'e', 'E', '+', etc.)
  event.preventDefault()
}

/**
 * Creates an onChange handler that sanitizes numeric input.
 */
export function createNumericChangeHandler(
  onChange: (value: string) => void,
): (event: React.ChangeEvent<HTMLInputElement>) => void {
  return (event) => onChange(sanitizeNumericInput(event.target.value))
}
